/* Sprint A — Audience Personas Panel.
 *
 * Operator-facing CRUD over `/api/projects/{id}/personas`. Surfaces
 * the persona data that is ALREADY consumed by Brand DNA / Research /
 * Business Plan / Concepts / Script / Storyboard / Proposal prompts
 * (via `research_context_builder.compact_persona_block`) but had no
 * UI until now.
 *
 * Honesty contract (mirrors backend persona.py):
 *   - Each persona carries `evidence_used[]` + `assumptions_requiring_confirmation[]`
 *   - Confidence pill: high/medium/low based on evidence_used count
 *   - "URL only" social rows are flagged inside evidence rows
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCircle, Plus, Trash2, RefreshCw, AlertCircle, ShieldCheck } from "lucide-react";

const CONF_TONE = {
    high:   "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    medium: "border-amber-500/30 text-amber-300 bg-amber-500/5",
    low:    "border-zinc-700 text-zinc-400 bg-zinc-900/40",
};

function ConfidencePill({ level }) {
    return (
        <span className={"text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm " + (CONF_TONE[level] || CONF_TONE.low)}
              data-testid={`persona-confidence-${level || "low"}`}>
            {level || "low"} confidence
        </span>
    );
}

export default function PersonasPanel({ projectId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        offer_or_service_name: "",
        persona_name: "",
        age_band: "",
        gender_mix: "",
        location_or_geography: "",
        income_level: "",
        intent: "",
        triggers: "",
        objections: "",
        preferred_channels: "",
        preferred_language: "",
    });

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/projects/${projectId}/personas`);
            setRows(data?.personas || []);
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Failed to load personas");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.offer_or_service_name.trim() || !form.persona_name.trim()) {
            toast.error("Offer name and persona name are required");
            return;
        }
        setCreating(true);
        try {
            const body = {
                offer_or_service_name: form.offer_or_service_name.trim(),
                persona_name: form.persona_name.trim(),
                age_band: form.age_band || undefined,
                gender_mix: form.gender_mix || undefined,
                location_or_geography: form.location_or_geography || undefined,
                income_level: form.income_level || undefined,
                intent: form.intent || undefined,
                triggers: form.triggers
                    ? form.triggers.split(",").map((s) => s.trim()).filter(Boolean)
                    : undefined,
                objections: form.objections
                    ? form.objections.split(",").map((s) => s.trim()).filter(Boolean)
                    : undefined,
                preferred_channels: form.preferred_channels
                    ? form.preferred_channels.split(",").map((s) => s.trim()).filter(Boolean)
                    : undefined,
                preferred_language: form.preferred_language || undefined,
            };
            await api.post(`/projects/${projectId}/personas`, body);
            toast.success("Persona saved");
            setForm({
                offer_or_service_name: "",
                persona_name: "",
                age_band: "",
                gender_mix: "",
                location_or_geography: "",
                income_level: "",
                intent: "",
                triggers: "",
                objections: "",
                preferred_channels: "",
                preferred_language: "",
            });
            load();
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Save failed");
        } finally { setCreating(false); }
    };

    const del = async (personaId) => {
        if (!confirm("Delete this persona?")) return;
        try {
            await api.delete(`/projects/${projectId}/personas/${personaId}`);
            toast.success("Persona deleted");
            load();
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Delete failed");
        }
    };

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="mb-6 border border-zinc-800 rounded-sm bg-zinc-950/40"
             data-testid="personas-panel">
            <div className="flex items-center justify-between gap-3 p-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-zinc-400" />
                    <div>
                        <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                            Audience Personas
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                            Already consumed by Brand DNA / Research / Business Plan / Script prompts.
                        </div>
                    </div>
                </div>
                <button type="button" onClick={load} disabled={loading}
                        data-testid="personas-refresh-btn"
                        className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-zinc-200 rounded-sm disabled:opacity-40 inline-flex items-center gap-1">
                    <RefreshCw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} />
                    Refresh
                </button>
            </div>

            {/* List */}
            <div className="p-3">
                {rows.length === 0 ? (
                    <div className="py-4 text-center text-xs text-zinc-500" data-testid="personas-empty">
                        No personas yet. Add one below — they will be honestly labelled and fed into all downstream agent prompts.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {rows.map((p) => (
                            <PersonaCard key={p.id} persona={p} onDelete={() => del(p.id)} />
                        ))}
                    </div>
                )}
            </div>

            {/* Create form */}
            <div className="p-3 border-t border-zinc-800 bg-zinc-950">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add persona
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    <Input testid="persona-form-offer" placeholder="Offer / service (required)" value={form.offer_or_service_name} onChange={setField("offer_or_service_name")} />
                    <Input testid="persona-form-name"  placeholder="Persona name (required)" value={form.persona_name} onChange={setField("persona_name")} />
                    <Input testid="persona-form-age"   placeholder="Age band (e.g. 25-35)" value={form.age_band} onChange={setField("age_band")} />
                    <Input testid="persona-form-gender" placeholder="Gender mix" value={form.gender_mix} onChange={setField("gender_mix")} />
                    <Input testid="persona-form-geo"   placeholder="Location / geography" value={form.location_or_geography} onChange={setField("location_or_geography")} />
                    <Input testid="persona-form-income" placeholder="Income level" value={form.income_level} onChange={setField("income_level")} />
                    <Input testid="persona-form-intent" placeholder="Intent (e.g. consider, ready-to-buy)" value={form.intent} onChange={setField("intent")} />
                    <Input testid="persona-form-lang"  placeholder="Preferred language" value={form.preferred_language} onChange={setField("preferred_language")} />
                    <Input testid="persona-form-triggers" placeholder="Triggers (comma sep)" value={form.triggers} onChange={setField("triggers")} />
                    <Input testid="persona-form-objections" placeholder="Objections (comma sep)" value={form.objections} onChange={setField("objections")} />
                    <Input testid="persona-form-channels" placeholder="Preferred channels (comma sep)" value={form.preferred_channels} onChange={setField("preferred_channels")} />
                </div>
                <button type="button" onClick={create} disabled={creating}
                        data-testid="persona-create-btn"
                        className="mt-3 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 rounded-sm disabled:opacity-40 inline-flex items-center gap-1">
                    {creating ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Add persona
                </button>
            </div>
        </div>
    );
}

function PersonaCard({ persona, onDelete }) {
    const p = persona || {};
    const conf = p.confidence_level || "low";
    return (
        <div className="border border-zinc-800 p-3 rounded-sm bg-zinc-950"
             data-testid={`persona-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">{p.persona_name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 font-mono uppercase tracking-wider">
                        {p.offer_or_service_name || "—"}
                    </div>
                </div>
                <button type="button" onClick={onDelete}
                        data-testid={`persona-delete-${p.id}`}
                        className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                <ConfidencePill level={conf} />
                {p.age_band ? <Chip label={p.age_band} /> : null}
                {p.location_or_geography ? <Chip label={p.location_or_geography} /> : null}
                {p.intent ? <Chip label={p.intent} /> : null}
            </div>
            {p.triggers?.length ? (
                <div className="mt-2 text-[11px] text-zinc-300">
                    <span className="text-zinc-500">Triggers: </span>{(p.triggers || []).join(", ")}
                </div>
            ) : null}
            {p.objections?.length ? (
                <div className="mt-1 text-[11px] text-zinc-300">
                    <span className="text-zinc-500">Objections: </span>{(p.objections || []).join(", ")}
                </div>
            ) : null}
            {p.preferred_channels?.length ? (
                <div className="mt-1 text-[11px] text-zinc-300">
                    <span className="text-zinc-500">Channels: </span>{(p.preferred_channels || []).join(", ")}
                </div>
            ) : null}
            {(p.evidence_used?.length || 0) > 0 ? (
                <div className="mt-2 pt-2 border-t border-zinc-800 text-[10px] text-zinc-500 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-500/70" />
                    {p.evidence_used.length} evidence source{p.evidence_used.length === 1 ? "" : "s"}
                </div>
            ) : null}
            {(p.assumptions_requiring_confirmation?.length || 0) > 0 ? (
                <div className="mt-1 text-[10px] text-amber-300/80 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 flex-none mt-0.5" />
                    <span>{p.assumptions_requiring_confirmation.length} assumption{p.assumptions_requiring_confirmation.length === 1 ? "" : "s"} require confirmation</span>
                </div>
            ) : null}
        </div>
    );
}

function Chip({ label }) {
    return (
        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 text-zinc-400 rounded-sm">
            {label}
        </span>
    );
}

function Input({ value, onChange, placeholder, testid }) {
    return (
        <input
            type="text"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            data-testid={testid}
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-sm px-2 py-1.5 text-xs focus:outline-none focus:border-zinc-600"
        />
    );
}
